// LOC1-G1-F1 sub-slice 7a (docs/build-briefs/LOC1-G1-F1-7a-legal-entity.md; DEC-301). The pure
// half of the Software Owner console's legal-details fields: the field list in the UX70 mockup's
// order (Entity, Contacts, Billing address), the checks that mirror what the server enforces,
// and the request the form builds. DOM-free so a plain node:test file can import it.
//
// TEN22 adds the EIN field (stored encrypted by the server, shown here only as a mask until the software
// owner presses Show). What is NOT here: the franchisor of record, liquor licenses, tax, billing method,
// plan and agreements. The server stays the authority on every
// check below; they exist so a person hears the plain reason before sending.
//
// The request carries only what changed against the profile that was loaded. A contact that was
// not touched is not re-sent, so saving does not add a new version of every contact.

/** The USPS codes the pickers offer. A state is picked, never typed. Mirrors
 * src/domain/legalEntities.ts's US_STATE_CODES; a test holds the two lists equal. */
export const US_STATE_CODES: readonly string[] = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "AS",
  "GU",
  "MP",
  "PR",
  "VI",
];

/** Picker labels, "NC - North Carolina". The codes are the stored values. */
export const US_STATE_NAMES: Readonly<Record<string, string>> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  AS: "American Samoa",
  GU: "Guam",
  MP: "Northern Mariana Islands",
  PR: "Puerto Rico",
  VI: "U.S. Virgin Islands",
};

export const LEGAL_TEXT_MAX_LENGTH = 200;

export type NeedTag = "golive" | "days30" | "optional";

export const NEED_TAG_LABELS: Record<NeedTag, string> = {
  golive: "Needed to go live",
  days30: "Needed in 30 days",
  optional: "Optional",
};

export interface LegalFormValues {
  legalName: string;
  dba: string;
  ein: string;
  stateOfFormation: string;
  regStreet: string;
  regCity: string;
  regState: string;
  regZip: string;
  primaryName: string;
  primaryTitle: string;
  primaryPhone: string;
  primaryEmail: string;
  signatoryName: string;
  signatoryTitle: string;
  signatoryEmail: string;
  signatoryPhone: string;
  billingContactName: string;
  billingContactEmail: string;
  billingContactPhone: string;
  operationalContactName: string;
  billStreet: string;
  billCity: string;
  billState: string;
  billZip: string;
}

export type LegalFieldKey = keyof LegalFormValues;

export const EMPTY_LEGAL_FORM: LegalFormValues = {
  legalName: "",
  dba: "",
  ein: "",
  stateOfFormation: "",
  regStreet: "",
  regCity: "",
  regState: "",
  regZip: "",
  primaryName: "",
  primaryTitle: "",
  primaryPhone: "",
  primaryEmail: "",
  signatoryName: "",
  signatoryTitle: "",
  signatoryEmail: "",
  signatoryPhone: "",
  billingContactName: "",
  billingContactEmail: "",
  billingContactPhone: "",
  operationalContactName: "",
  billStreet: "",
  billCity: "",
  billState: "",
  billZip: "",
};

export type LegalGroupId = "entity" | "contacts" | "billing";

export interface LegalGroupDef {
  id: LegalGroupId;
  title: string;
}

/** The mockup's step titles, in order. Steps 4, 5, 7 and 8 and the EIN are not built. */
export const LEGAL_GROUPS: readonly LegalGroupDef[] = [
  { id: "entity", title: "Entity" },
  { id: "contacts", title: "Contacts" },
  { id: "billing", title: "Billing address" },
];

export type LegalFieldKind = "text" | "tel" | "email" | "zip" | "state" | "ein";

export interface LegalFieldDef {
  key: LegalFieldKey;
  group: LegalGroupId;
  label: string;
  kind: LegalFieldKind;
  need: NeedTag;
  placeholder?: string;
  /** Spans both columns. */
  wide?: boolean;
}

/** The UX70 mockup's field order. Need tags follow its Step 2, 3 and 6 labels. */
export const LEGAL_FIELDS: readonly LegalFieldDef[] = [
  {
    key: "legalName",
    group: "entity",
    label: "Legal name",
    kind: "text",
    need: "golive",
    placeholder: "e.g. Queen City Lounges LLC",
  },
  {
    key: "dba",
    group: "entity",
    label: "DBA (legal)",
    kind: "text",
    need: "golive",
    placeholder: "Trade name, if different",
  },
  {
    key: "ein",
    group: "entity",
    label: "EIN",
    kind: "ein",
    need: "golive",
    placeholder: "12-3456789",
  },
  {
    key: "stateOfFormation",
    group: "entity",
    label: "Formation state",
    kind: "state",
    need: "golive",
  },
  {
    key: "regStreet",
    group: "entity",
    label: "Registered address",
    kind: "text",
    need: "golive",
    placeholder: "Street address",
    wide: true,
  },
  { key: "regCity", group: "entity", label: "City", kind: "text", need: "golive" },
  { key: "regState", group: "entity", label: "State", kind: "state", need: "golive" },
  { key: "regZip", group: "entity", label: "ZIP", kind: "zip", need: "golive" },
  {
    key: "primaryName",
    group: "contacts",
    label: "Primary name",
    kind: "text",
    need: "days30",
    placeholder: "Full name",
  },
  {
    key: "primaryTitle",
    group: "contacts",
    label: "Primary title",
    kind: "text",
    need: "days30",
    placeholder: "e.g. General Manager",
  },
  {
    key: "primaryPhone",
    group: "contacts",
    label: "Phone number",
    kind: "tel",
    need: "days30",
    placeholder: "(555) 555-5555",
  },
  {
    key: "primaryEmail",
    group: "contacts",
    label: "Email address",
    kind: "email",
    need: "days30",
    placeholder: "name@example.com",
    wide: true,
  },
  {
    key: "signatoryName",
    group: "contacts",
    label: "Signatory name",
    kind: "text",
    need: "golive",
    placeholder: "Full name",
  },
  {
    key: "signatoryTitle",
    group: "contacts",
    label: "Signatory title",
    kind: "text",
    need: "golive",
    placeholder: "e.g. Managing Member",
  },
  {
    key: "signatoryEmail",
    group: "contacts",
    label: "Signatory email",
    kind: "email",
    need: "golive",
    placeholder: "name@example.com",
  },
  {
    key: "signatoryPhone",
    group: "contacts",
    label: "Signatory phone (optional)",
    kind: "tel",
    need: "golive",
    placeholder: "(555) 555-5555",
  },
  {
    key: "billingContactName",
    group: "contacts",
    label: "Billing contact",
    kind: "text",
    need: "optional",
    placeholder: "If different from the primary contact",
  },
  {
    key: "billingContactEmail",
    group: "billing",
    label: "Billing email",
    kind: "email",
    need: "optional",
  },
  {
    key: "billingContactPhone",
    group: "billing",
    label: "Billing phone",
    kind: "tel",
    need: "optional",
  },
  {
    key: "operationalContactName",
    group: "contacts",
    label: "Operational contact",
    kind: "text",
    need: "optional",
    placeholder: "If different from the primary contact",
  },
  {
    key: "billStreet",
    group: "billing",
    label: "Street address",
    kind: "text",
    need: "optional",
    placeholder: "Street address",
    wide: true,
  },
  { key: "billCity", group: "billing", label: "City", kind: "text", need: "optional" },
  { key: "billState", group: "billing", label: "State", kind: "state", need: "optional" },
  { key: "billZip", group: "billing", label: "ZIP", kind: "zip", need: "optional" },
];

// ---------------------------------------------------------------------------
// What the server sends and receives
// ---------------------------------------------------------------------------

export interface AddressDto {
  street: string;
  city: string;
  state: string;
  zip: string;
}

export interface ContactDto {
  versionNo: number;
  name: string;
  title: string | null;
  phone: string | null;
  email: string | null;
}

export type ProfileMissing = "legal_entity" | "primary_contact" | "authorized_signatory";

export interface LegalProfileStatusDto {
  status: "incomplete" | "complete";
  missing: ProfileMissing[];
}

/** The EIN as the server sends it: whether one is recorded and its mask, never the value. Null when the
 * server cannot read it (no provisioning credential). */
export interface EinDto {
  present: boolean;
  masked: string | null;
}

/** Registered details are either complete or explicitly absent in the response. */
export type RegisteredAddressViewDto =
  AddressDto | { street: null; city: null; state: null; zip: null };

export interface LegalProfileDto {
  tenantId: string;
  ein?: EinDto | null;
  profile: LegalProfileStatusDto;
  entity: {
    legalEntityId: string;
    versionNo: number;
    legalName: string;
    dba: string | null;
    stateOfFormation: string | null;
    registeredAddress: RegisteredAddressViewDto;
    billingAddress: AddressDto | null;
  } | null;
  contacts: {
    primary: ContactDto | null;
    authorizedSignatory: ContactDto | null;
    billing: ContactDto | null;
    operational: ContactDto | null;
  };
}

export interface LegalRequest {
  /** Onboarding only: the typed EIN, stored by the server after the legal entity. */
  ein?: string;
  entity?: {
    legalName: string;
    dba: string | null;
    stateOfFormation: string;
    registeredAddress: AddressDto;
    billingAddress: AddressDto | null;
  };
  contacts?: Partial<
    Record<
      "primary" | "authorizedSignatory" | "billing" | "operational",
      | { name: string; title: string | null; phone: string | null; email: string | null }
      | { removed: true }
    >
  >;
}

// ---------------------------------------------------------------------------
// Form values from and to the server's shapes
// ---------------------------------------------------------------------------

function tidy(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function blank(value: string): boolean {
  return tidy(value).length === 0;
}

/** The loaded profile as form values, so the panel opens filled in. */
export function formValuesFromProfile(dto: LegalProfileDto): LegalFormValues {
  const values: LegalFormValues = { ...EMPTY_LEGAL_FORM };
  const entity = dto.entity;
  if (entity !== null) {
    values.legalName = entity.legalName;
    values.dba = entity.dba ?? "";
    values.stateOfFormation = entity.stateOfFormation ?? "";
    values.regStreet = entity.registeredAddress.street ?? "";
    values.regCity = entity.registeredAddress.city ?? "";
    values.regState = entity.registeredAddress.state ?? "";
    values.regZip = entity.registeredAddress.zip ?? "";
    if (entity.billingAddress !== null) {
      values.billStreet = entity.billingAddress.street;
      values.billCity = entity.billingAddress.city;
      values.billState = entity.billingAddress.state;
      values.billZip = entity.billingAddress.zip;
    }
  }
  const { primary, authorizedSignatory, billing, operational } = dto.contacts;
  if (primary !== null) {
    values.primaryName = primary.name;
    values.primaryTitle = primary.title ?? "";
    values.primaryPhone = primary.phone ?? "";
    values.primaryEmail = primary.email ?? "";
  }
  if (authorizedSignatory !== null) {
    values.signatoryName = authorizedSignatory.name;
    values.signatoryTitle = authorizedSignatory.title ?? "";
    values.signatoryEmail = authorizedSignatory.email ?? "";
    values.signatoryPhone = authorizedSignatory.phone ?? "";
  }
  values.billingContactName = billing?.name ?? "";
  values.billingContactEmail = billing?.email ?? "";
  values.billingContactPhone = billing?.phone ?? "";
  values.operationalContactName = operational?.name ?? "";
  return values;
}

/** TEN8-F1-F4: true when the signatory is the primary contact, so the "Someone else signs" switch is off. A
 * profile with no signatory recorded reads as off too. */
export function signatoryIsPrimary(values: LegalFormValues): boolean {
  if (blank(values.signatoryName) && blank(values.signatoryTitle)) {
    return true;
  }
  return (
    tidy(values.signatoryName) === tidy(values.primaryName) &&
    tidy(values.signatoryTitle) === tidy(values.primaryTitle)
  );
}

/** The values to validate and save: with the switch off, the signatory is the primary contact's name and title. */
export function resolveSignatory(
  values: LegalFormValues,
  someoneElseSigns: boolean,
): LegalFormValues {
  return someoneElseSigns
    ? values
    : {
        ...values,
        signatoryName: values.primaryName,
        signatoryTitle: values.primaryTitle,
        signatoryEmail: "",
        signatoryPhone: "",
      };
}

/** With the switch off the signatory fields are not on screen, so an error against one moves to the primary field
 * the person can see (unless that field already carries its own). */
export function moveHiddenSignatoryErrors(
  errors: LegalFormErrors,
  someoneElseSigns: boolean,
): LegalFormErrors {
  if (someoneElseSigns) {
    return errors;
  }
  const moved: LegalFormErrors = { ...errors };
  delete moved.signatoryName;
  delete moved.signatoryTitle;
  delete moved.signatoryEmail;
  delete moved.signatoryPhone;
  if (errors.signatoryName !== undefined && moved.primaryName === undefined) {
    moved.primaryName = "Enter the primary contact's name.";
  }
  if (errors.signatoryTitle !== undefined && moved.primaryTitle === undefined) {
    moved.primaryTitle = "Enter the primary contact's title.";
  }
  return moved;
}

function entityStarted(v: LegalFormValues): boolean {
  return [
    v.legalName,
    v.dba,
    v.stateOfFormation,
    v.regStreet,
    v.regCity,
    v.regState,
    v.regZip,
  ].some((value) => !blank(value));
}

function primaryStarted(v: LegalFormValues): boolean {
  return [v.primaryName, v.primaryTitle, v.primaryPhone, v.primaryEmail].some((x) => !blank(x));
}

function signatoryStarted(v: LegalFormValues): boolean {
  return [v.signatoryName, v.signatoryTitle].some((x) => !blank(x));
}

function billingStarted(v: LegalFormValues): boolean {
  return [v.billStreet, v.billCity, v.billState, v.billZip].some((x) => !blank(x));
}

const ZIP = /^[0-9]{5}(-[0-9]{4})?$/;
/** Mirrors normalizeEin in src/domain/taxIdentifierFormat.ts: nine digits, one optional hyphen after two. */
const EIN = /^[0-9]{2}-?[0-9]{7}$/;
const EMAIL = /^[^@\s]+@[^@\s]+$/;
const PHONE_CHARACTERS = /^[0-9 ()+.\-x]+$/;

export type LegalFormErrors = Partial<Record<LegalFieldKey, string>>;
/** What the DOM half accepts for showing errors. */
export type LegalErrorsLike = LegalFormErrors;

function tooLong(value: string): boolean {
  return tidy(value).length > LEGAL_TEXT_MAX_LENGTH;
}

/** The checks, in plain words. `loaded` is the profile the panel opened with, or null on the
 * onboarding form; it decides whether an emptied required contact is a removal (refused). */
export function validateLegalForm(
  values: LegalFormValues,
  loaded: LegalProfileDto | null,
  someoneElseSigns = false,
): LegalFormErrors {
  const errors: LegalFormErrors = {};
  for (const field of LEGAL_FIELDS) {
    if (field.kind !== "state" && tooLong(values[field.key])) {
      errors[field.key] = `Use ${LEGAL_TEXT_MAX_LENGTH} characters or fewer.`;
    }
  }
  const need = (key: LegalFieldKey, message: string): void => {
    if (blank(values[key]) && errors[key] === undefined) {
      errors[key] = message;
    }
  };
  if (entityStarted(values)) {
    need("legalName", "Enter the legal name.");
    need("stateOfFormation", "Choose the state of formation.");
    need("regStreet", "Enter the street address.");
    need("regCity", "Enter the city.");
    need("regState", "Choose the state.");
    need("regZip", "Enter the ZIP.");
  } else if (loaded?.entity !== null && loaded?.entity !== undefined) {
    errors.legalName = "The legal entity cannot be removed. Change its details instead.";
  }
  if (!blank(values.regZip) && !ZIP.test(tidy(values.regZip))) {
    errors.regZip = "Use five digits, or ZIP+4 like 28202-1234.";
  }
  if (!blank(values.ein)) {
    if (!EIN.test(tidy(values.ein))) {
      errors.ein = "Enter the EIN as nine digits, like 12-3456789.";
    } else if (!entityStarted(values) && (loaded?.entity ?? null) === null) {
      errors.ein = "Enter the legal name and address first. The EIN belongs to the legal entity.";
    }
  }
  if (primaryStarted(values)) {
    need("primaryName", "Enter the primary contact's name.");
    need("primaryPhone", "Enter a phone number.");
    need("primaryEmail", "Enter an email address.");
  } else if (loaded?.contacts.primary !== null && loaded?.contacts.primary !== undefined) {
    errors.primaryName = "The primary contact cannot be removed. Replace the person instead.";
  }
  if (!blank(values.primaryPhone) && errors.primaryPhone === undefined) {
    const phone = tidy(values.primaryPhone);
    const digits = phone.replace(/[^0-9]/g, "").length;
    if (!PHONE_CHARACTERS.test(phone) || digits < 7 || digits > 15) {
      errors.primaryPhone = "Enter a phone number with 7 to 15 digits.";
    }
  }
  if (!blank(values.primaryEmail) && errors.primaryEmail === undefined) {
    if (!EMAIL.test(tidy(values.primaryEmail))) {
      errors.primaryEmail = "Enter an email like name@example.com.";
    }
  }
  if (!blank(values.billingContactEmail) && !EMAIL.test(tidy(values.billingContactEmail))) {
    errors.billingContactEmail = "Enter an email like name@example.com.";
  }
  if (!blank(values.billingContactPhone)) {
    const phone = tidy(values.billingContactPhone);
    const digits = phone.replace(/[^0-9]/g, "").length;
    if (!PHONE_CHARACTERS.test(phone) || digits < 7 || digits > 15) {
      errors.billingContactPhone = "Enter a phone number with 7 to 15 digits.";
    }
  }
  if (
    blank(values.billingContactName) &&
    (!blank(values.billingContactEmail) || !blank(values.billingContactPhone))
  ) {
    errors.billingContactName = "Enter the billing contact's name.";
  }
  if (someoneElseSigns || signatoryStarted(values)) {
    need("signatoryName", "Enter the authorized signatory's name.");
    need("signatoryTitle", "Enter the authorized signatory's title.");
    // SIGNATORY1: the server takes the signatory's email as optional, but the agreement has to reach them, so the
    // form asks for it whenever a different person is named. A recorded signatory keeps their email without retyping.
    const recorded = loaded?.contacts.authorizedSignatory ?? null;
    const keepsRecorded = recorded !== null && tidy(recorded.name) === tidy(values.signatoryName);
    if (someoneElseSigns && !keepsRecorded) {
      need("signatoryEmail", "Enter the signatory's email address.");
      // The form opens with the recorded person's email; naming someone new must not quietly keep it.
      if (
        recorded !== null &&
        recorded.email !== null &&
        tidy(values.signatoryEmail).toLowerCase() === recorded.email.toLowerCase()
      ) {
        errors.signatoryEmail = "Enter the new signatory's email address, not the previous signatory's.";
      }
    }
    if (!blank(values.signatoryEmail) && !EMAIL.test(tidy(values.signatoryEmail))) {
      errors.signatoryEmail = "Enter an email like name@example.com.";
    }
    if (!blank(values.signatoryPhone)) {
      const phone = tidy(values.signatoryPhone);
      const digits = phone.replace(/[^0-9]/g, "").length;
      if (!PHONE_CHARACTERS.test(phone) || digits < 7 || digits > 15) {
        errors.signatoryPhone = "Enter a phone number with 7 to 15 digits.";
      }
    }
  } else if (
    loaded?.contacts.authorizedSignatory !== null &&
    loaded?.contacts.authorizedSignatory !== undefined
  ) {
    errors.signatoryName =
      "The authorized signatory cannot be removed. Replace the person instead.";
  }
  if (billingStarted(values)) {
    need("billStreet", "Enter the billing street address, or clear the billing address.");
    need("billCity", "Enter the city.");
    need("billState", "Choose the state.");
    need("billZip", "Enter the ZIP.");
    if (!blank(values.billZip) && !ZIP.test(tidy(values.billZip))) {
      errors.billZip = "Use five digits, or ZIP+4 like 28202-1234.";
    }
  }
  return errors;
}

export function hasLegalErrors(errors: LegalFormErrors): boolean {
  return Object.keys(errors).length > 0;
}

function addressOf(street: string, city: string, state: string, zip: string): AddressDto {
  return {
    street: tidy(street),
    city: tidy(city),
    state: state.trim().toUpperCase(),
    zip: tidy(zip),
  };
}

function textOrNull(value: string): string | null {
  return blank(value) ? null : tidy(value);
}

function completeAddress(address: RegisteredAddressViewDto): address is AddressDto {
  return (
    typeof address.street === "string" &&
    typeof address.city === "string" &&
    typeof address.state === "string" &&
    typeof address.zip === "string"
  );
}

function sameAddress(a: RegisteredAddressViewDto | null, b: AddressDto | null): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  if (!completeAddress(a) || !completeAddress(b)) return false;
  return a.street === b.street && a.city === b.city && a.state === b.state && a.zip === b.zip;
}

/**
 * The request for what the person changed, or an empty object when nothing changed. Run
 * validateLegalForm first; this assumes the values passed it.
 */
export function buildLegalRequest(
  values: LegalFormValues,
  loaded: LegalProfileDto | null,
): LegalRequest {
  const request: LegalRequest = {};
  if (entityStarted(values)) {
    const registered = addressOf(values.regStreet, values.regCity, values.regState, values.regZip);
    const billing = billingStarted(values)
      ? addressOf(values.billStreet, values.billCity, values.billState, values.billZip)
      : null;
    const wanted = {
      legalName: tidy(values.legalName),
      dba: textOrNull(values.dba),
      stateOfFormation: values.stateOfFormation.trim().toUpperCase(),
      registeredAddress: registered,
      billingAddress: billing,
    };
    const current = loaded?.entity ?? null;
    const unchanged =
      current !== null &&
      current.legalName === wanted.legalName &&
      current.dba === wanted.dba &&
      current.stateOfFormation === wanted.stateOfFormation &&
      sameAddress(current.registeredAddress, wanted.registeredAddress) &&
      sameAddress(current.billingAddress, wanted.billingAddress);
    if (!unchanged) {
      request.entity = wanted;
    }
  }
  const contacts: NonNullable<LegalRequest["contacts"]> = {};
  const same = (a: string | null | undefined, b: string | null): boolean => (a ?? null) === b;
  if (primaryStarted(values)) {
    const wanted = {
      name: tidy(values.primaryName),
      title: textOrNull(values.primaryTitle),
      phone: textOrNull(values.primaryPhone),
      email: textOrNull(values.primaryEmail)?.toLowerCase() ?? null,
    };
    const current = loaded?.contacts.primary ?? null;
    if (
      current === null ||
      current.name !== wanted.name ||
      !same(current.title, wanted.title) ||
      !same(current.phone, wanted.phone) ||
      !same(current.email, wanted.email)
    ) {
      contacts.primary = wanted;
    }
  }
  if (signatoryStarted(values)) {
    const current = loaded?.contacts.authorizedSignatory ?? null;
    const name = tidy(values.signatoryName);
    const title = textOrNull(values.signatoryTitle);
    // The person is unchanged when only the title moved, so a detail left blank here carries forward.
    const samePerson = current !== null && current.name === name;
    const email =
      textOrNull(values.signatoryEmail)?.toLowerCase() ?? (samePerson ? current.email : null);
    const phone = textOrNull(values.signatoryPhone) ?? (samePerson ? current.phone : null);
    if (
      current === null ||
      current.name !== name ||
      !same(current.title, title) ||
      !same(current.email, email) ||
      !same(current.phone, phone)
    ) {
      contacts.authorizedSignatory = { name, title, phone, email };
    }
  }
  const currentBilling = loaded?.contacts.billing ?? null;
  if (!blank(values.billingContactName)) {
    const name = tidy(values.billingContactName);
    const phone = textOrNull(values.billingContactPhone);
    const email = textOrNull(values.billingContactEmail)?.toLowerCase() ?? null;
    if (
      currentBilling === null ||
      currentBilling.name !== name ||
      !same(currentBilling.phone, phone) ||
      !same(currentBilling.email, email)
    ) {
      contacts.billing = { name, title: currentBilling?.title ?? null, phone, email };
    }
  } else if (currentBilling !== null) contacts.billing = { removed: true };
  const currentOperational = loaded?.contacts.operational ?? null;
  if (!blank(values.operationalContactName)) {
    const name = tidy(values.operationalContactName);
    if (currentOperational === null || currentOperational.name !== name) {
      contacts.operational = { name, title: null, phone: null, email: null };
    }
  } else if (currentOperational !== null) contacts.operational = { removed: true };
  if (Object.keys(contacts).length > 0) {
    request.contacts = contacts;
  }
  return request;
}

export function legalRequestIsEmpty(request: LegalRequest): boolean {
  return request.entity === undefined && request.contacts === undefined;
}

/** The EIN to send, digits and hyphen as typed, or null when the field holds nothing to save (it is blank, or it
 * still shows the recorded mask). Run validateLegalForm first. */
export function einToSave(values: LegalFormValues): string | null {
  return blank(values.ein) ? null : tidy(values.ein);
}

/** A revealed EIN as people write it, 12-3456789. Anything that is not nine digits is shown as received. */
export function formatEin(value: string): string {
  return /^[0-9]{9}$/.test(value) ? `${value.slice(0, 2)}-${value.slice(2)}` : value;
}

/** The Onboard form's legal request: the entity and contacts, plus the EIN when one was typed. */
export function buildOnboardingLegalRequest(values: LegalFormValues): LegalRequest {
  const request = buildLegalRequest(values, null);
  const ein = einToSave(values);
  return ein === null ? request : { ...request, ein };
}

// ---------------------------------------------------------------------------
// Wording
// ---------------------------------------------------------------------------

export interface ProfileBadge {
  label: string;
  tone: "good" | "warn";
}

export function profileBadge(status: LegalProfileStatusDto): ProfileBadge {
  return status.status === "complete"
    ? { label: "Legal profile complete", tone: "good" }
    : { label: "Legal profile incomplete", tone: "warn" };
}

const MISSING_TEXT: Record<ProfileMissing, string> = {
  legal_entity: "the legal entity",
  primary_contact: "the primary contact",
  authorized_signatory: "the authorized signatory",
};

/** "Still needed: the legal entity, the primary contact and the authorized signatory." */
export function missingText(status: LegalProfileStatusDto): string {
  if (status.status === "complete" || status.missing.length === 0) {
    return "Legal profile complete.";
  }
  const parts = status.missing.map((part) => MISSING_TEXT[part]);
  const joined =
    parts.length === 1
      ? (parts[0] as string)
      : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1] as string}`;
  return `Still needed: ${joined}.`;
}

/** One plain sentence for a failed legal read or save, by what the server said. */
export function legalErrorMessage(
  status: number,
  body: { message?: string } | undefined,
  action: "load" | "save",
): string {
  const serverMessage = body?.message;
  if (status === 0) {
    return "Could not reach the server. Check the connection and try again.";
  }
  if (status === 401) {
    return "Your session has ended. Sign in again.";
  }
  if (status === 404) {
    return "That customer was not found.";
  }
  if (
    (status === 400 || status === 409 || status === 503) &&
    serverMessage !== undefined &&
    serverMessage.length > 0
  ) {
    return serverMessage;
  }
  return action === "save"
    ? "The legal details could not be saved. Nothing was changed. Try again."
    : "The legal details could not be loaded. Try again.";
}
